// useSearch — TanStack Query hook over `GET /api/search` (RFC-0011 Task 1.3;
// RFC-0007 contract; ADR-0007). Shared by the Cmd-K palette and the `/search`
// page. Mirrors the `lib/queries/*` read hooks: a queryKey factory + an `apiGet`
// queryFn, inheriting the shared `shouldRetryRead` retry policy from queryClient.
//
// Behavior specific to search:
//   - 200ms DEBOUNCE on the free-text `q`: the live query is held in local state
//     and only committed into the queryKey after typing pauses, so `useQuery`
//     refetches once per settled query rather than per keystroke. Structural
//     params (repo/type/owner/page) change on discrete clicks and commit at once.
//   - `placeholderData: keepPreviousData` keeps prior results on screen while the
//     debounced key settles (no flash to empty while typing).
//   - CANCELLATION via TanStack's per-execution `signal`, forwarded to `apiGet`
//     (Task 1.1). A superseded key aborts its in-flight request; `apiGet`
//     rethrows the AbortError as-is so it isn't retried.
//   - `enabled` only when `q` is non-empty (after trim) → an empty query drives
//     the Idle state with no request.

import { useEffect, useState } from 'react';
import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { SearchResponse } from '@portal/shared-types';
import { apiGet, type QueryParams } from '@/lib/api-client';

/** camelCase params accepted by `useSearch` (mapped to RFC-0007 query params). */
export interface SearchParams {
  q: string;
  /** Repo SHORT name; omit for "all projects" scope. */
  repo?: string;
  type?: string;
  /** Frontmatter status facet (RFC-0007 `status` query param). */
  status?: string;
  owner?: string;
  page?: number;
  perPage?: number;
}

/** Query-key factory (mirrors `projectKeys`) so families can be invalidated. */
export const searchKeys = {
  all: ['search'] as const,
  results: (params: SearchParams) => [...searchKeys.all, params] as const,
};

/** Map the camelCase hook params onto RFC-0007's snake_case query params. */
function toApiParams(params: SearchParams): QueryParams {
  return {
    q: params.q,
    repo: params.repo,
    type: params.type,
    status: params.status,
    owner: params.owner,
    page: params.page,
    per_page: params.perPage,
  };
}

/** Debounce window before a changed `q` commits into the queryKey. */
export const SEARCH_DEBOUNCE_MS = 200;

/**
 * Debounced, cancellable search query. Returns the standard TanStack Query
 * result; consumers read `data` (`SearchResponse`), `isPending`/`isFetching` for
 * loading, and `error` (`ApiError` with `code` `invalid_query`/`search_unavailable`,
 * or `NetworkError`). A 401 is handled upstream by `apiGet`'s interceptor.
 */
export function useSearch(params: SearchParams): UseQueryResult<SearchResponse, Error> {
  // Debounce only the free-text `q`; structural params commit immediately.
  const [debouncedQ, setDebouncedQ] = useState(params.q);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(params.q), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [params.q]);

  const committed: SearchParams = { ...params, q: debouncedQ };
  const enabled = debouncedQ.trim().length > 0;

  return useQuery({
    queryKey: searchKeys.results(committed),
    queryFn: ({ signal }) =>
      apiGet<SearchResponse>('/api/search', toApiParams(committed), { signal }),
    enabled,
    placeholderData: keepPreviousData,
  });
}
